import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ProgressionService from '../../services/api/ProgressionService'
import { isValidIsoDate } from '../../utils/dateHelpers'
import { updateUrlFilters, useUrlFilters } from '../../utils/urlFilters'
import SearchInput from '../ui/SearchInput'
import Select from '../ui/Select/Select'
import Tabs from '../ui/Tabs'
import OperatorsActivityLogTable from './OperatorsActivityLogTable'
import ControlsAlertsChart from './ControlsAlertsChart'
import OperatorsProgressionTable from './OperatorsProgressionTable'
import useDebounceCallback from '../../hooks/useDebounceCallback'

const OPERATORS_TAB = 'operators'
const ACTIVITY_LOG_TAB = 'activity_log'
const DETAIL_TABS = [
  { value: OPERATORS_TAB, label: 'Opérateurs' },
  { value: ACTIVITY_LOG_TAB, label: 'Journal d\'activité' }
]
const TAB_PARAM = 'tab'

const emptyChart = { controls: [], alerts: [], alertsRate: [], satisfaction: [] }

const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Quotidien' },
  { value: 'week', label: 'Hebdomadaire' },
  { value: 'month', label: 'Mensuel' }
]

const DRAFT_GRANULARITY = { value: 'draft', label: 'Par planification' }
const MONTH_GRANULARITY = GRANULARITY_OPTIONS.find(option => option.value === 'month')

// Cross-drafts default to draft granularity (one point per planification); in a draft fall back to monthly.
const defaultGranularityFor = draftId => draftId ? MONTH_GRANULARITY : DRAFT_GRANULARITY

const SEARCH_PARAM = 'q'

const fetchChartData = (collectId, draftId, baseParams) => {
  const measureFor = name => ProgressionService.fetchMeasure(collectId, draftId, { name, ...baseParams })
  return Promise.all([
    measureFor('controls'),
    measureFor('alerts'),
    measureFor('alerts_rate'),
    measureFor('satisfaction')
  ]).then(([controls, alerts, alertsRate, satisfaction]) => ({
    controls: controls || [],
    alerts: alerts || [],
    alertsRate: alertsRate || [],
    satisfaction: satisfaction || []
  }))
}

const DISPLAY_MODES = ['absolute', 'percent']
const DEFAULT_DISPLAY_MODE = 'absolute'

const ProgressionContent = ({ collectId, draftId, dateRangeMin, dateRangeMax, variables }) => {
  const filters = useUrlFilters()
  const [chartData, setChartData] = useState(emptyChart)
  const [isChartLoading, setIsChartLoading] = useState(true)
  const [hasAlertsConfigured, setHasAlertsConfigured] = useState(false)
  const [rawOperatorOptions, setRawOperatorOptions] = useState([])
  const [searchInput, setSearchInput] = useState(filters[SEARCH_PARAM] || '')
  const activeTab = filters[TAB_PARAM] === ACTIVITY_LOG_TAB ? ACTIVITY_LOG_TAB : OPERATORS_TAB
  const displayMode = DISPLAY_MODES.includes(filters.display) ? filters.display : DEFAULT_DISPLAY_MODE

  const { start_date: filterStart, end_date: filterEnd } = filters
  const dateRange = useMemo(() => ({
    startDate: isValidIsoDate(filterStart) ? filterStart : dateRangeMin,
    endDate: isValidIsoDate(filterEnd) ? filterEnd : dateRangeMax
  }), [filterStart, filterEnd, dateRangeMin, dateRangeMax])
  const operatorId = filters.operator || ''
  const variableId = filters.variable || ''
  const resourceSearch = filters[SEARCH_PARAM] || ''
  const granularityOptions = useMemo(() => {
    if (draftId)
      return GRANULARITY_OPTIONS
    return [...GRANULARITY_OPTIONS, DRAFT_GRANULARITY]
  }, [draftId])
  const granularity = useMemo(() => {
    const found = granularityOptions.find(option => option.value === filters.granularity)
    return found || defaultGranularityFor(draftId)
  }, [filters.granularity, granularityOptions, draftId])

  useEffect(() => {
    let cancelled = false
    const { startDate, endDate } = dateRange
    const chartParams = { startDate, endDate, granularity: granularity.value }
    if (operatorId)
      chartParams.operatorId = operatorId
    if (resourceSearch)
      chartParams.resourceSearch = resourceSearch
    const guard = handler => result => {
      if (!cancelled)
        handler(result)
    }
    setIsChartLoading(true)
    fetchChartData(collectId, draftId, chartParams)
      .then(guard(setChartData))
      .catch(guard(() => setChartData(emptyChart)))
      .finally(guard(() => setIsChartLoading(false)))
    return () => { cancelled = true }
  }, [collectId, draftId, dateRange, granularity, operatorId, resourceSearch])

  useEffect(() => {
    let cancelled = false
    const guard = handler => result => {
      if (!cancelled)
        handler(result)
    }
    ProgressionService.fetchOperatorOptions(collectId, draftId)
      .then(guard(setRawOperatorOptions))
      .catch(guard(() => setRawOperatorOptions([])))
    ProgressionService.fetchMeta(collectId, draftId)
      .then(guard(meta => setHasAlertsConfigured(meta?.has_alerts_configured || false)))
      .catch(guard(() => setHasAlertsConfigured(false)))
    return () => { cancelled = true }
  }, [collectId, draftId])

  useEffect(() => {
    const next = filters[SEARCH_PARAM] || ''
    setSearchInput(current => next === current ? current : next)
  }, [filters[SEARCH_PARAM]])

  useEffect(() => {
    const tabValue = filters[TAB_PARAM]
    if (tabValue && tabValue !== ACTIVITY_LOG_TAB)
      updateUrlFilters({ [TAB_PARAM]: null })
  }, [filters[TAB_PARAM]])

  const commitSearchToUrl = useDebounceCallback(() => {
    updateUrlFilters({ [SEARCH_PARAM]: searchInput })
  }, 300)

  useEffect(() => { commitSearchToUrl() }, [searchInput, commitSearchToUrl])

  const operatorOptions = useMemo(
    () => rawOperatorOptions.map(operatorOption => (
      { value: operatorOption.user_id, label: operatorOption.user_name }
    )), [rawOperatorOptions]
  )

  const selectedOperator = useMemo(() => {
    const match = operatorOptions.find(option => String(option.value) === operatorId)
    return match || null
  }, [operatorOptions, operatorId])

  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])
  const handleOperatorFilterChange = useCallback(option => {
    updateUrlFilters({ operator: option?.value })
  }, [])
  const handleVariableChange = useCallback(option => {
    updateUrlFilters({ variable: option?.value })
  }, [])
  const handleGranularityChange = useCallback(option => {
    const fallback = defaultGranularityFor(draftId).value
    const value = option ? option.value : fallback
    updateUrlFilters({ granularity: value === fallback ? null : value })
  }, [draftId])
  const handleDisplayModeChange = useCallback(mode => {
    updateUrlFilters({ display: mode === DEFAULT_DISPLAY_MODE ? null : mode })
  }, [])
  const handleTabChange = useCallback(nextTab => {
    updateUrlFilters({ [TAB_PARAM]: nextTab === OPERATORS_TAB ? null : nextTab })
  }, [])

  return (
    <div>
      <div className="tw:flex tw:items-center tw:justify-end tw:mb-4 tw:gap-3">
        <SearchInput
          className="tw:grow"
          name={SEARCH_PARAM}
          placeholder="Rechercher une ressource..."
          value={searchInput}
          onChange={handleSearchChange}
        />
        <div className="tw:w-64">
          <Select
            options={operatorOptions}
            placeholder="Opérateurs"
            value={selectedOperator}
            isClearable
            onChange={handleOperatorFilterChange}
          />
        </div>
      </div>
      <ControlsAlertsChart
        alerts={chartData.alerts}
        controls={chartData.controls}
        displayMode={displayMode}
        hasAlertsConfigured={hasAlertsConfigured}
        isLoading={isChartLoading}
        satisfaction={chartData.satisfaction}
        headerRight={(
          <div className="tw:w-64">
            <Select
              options={granularityOptions}
              placeholder="Pas"
              value={granularity}
              isSearchable={false}
              onChange={handleGranularityChange}
            />
          </div>
        )}
        onDisplayModeChange={handleDisplayModeChange}
      />
      <Tabs
        className="tw:my-4"
        items={DETAIL_TABS}
        value={activeTab}
        onChange={handleTabChange}
      />
      <div className={activeTab === OPERATORS_TAB ? '' : 'tw:hidden'}>
        <OperatorsProgressionTable
          collectId={collectId}
          dateRange={dateRange}
          draftId={draftId}
          operatorId={operatorId}
          resourceSearch={resourceSearch}
          variableId={variableId}
          variables={variables}
          onVariableChange={handleVariableChange}
        />
      </div>
      <div className={activeTab === ACTIVITY_LOG_TAB ? '' : 'tw:hidden'}>
        <OperatorsActivityLogTable
          collectId={collectId}
          dateRange={dateRange}
          draftId={draftId}
          operatorId={operatorId}
          resourceSearch={resourceSearch}
        />
      </div>
    </div>
  )
}

export default ProgressionContent
